/*
 * Calendar days, in the business's own time zone.
 *
 * CLAUDE.md §4 says a day that means a day is a `date`, and the column types
 * follow that. This file closes the other end of the same problem: **working out
 * which day "today" is**.
 *
 * `new Date().toISOString().slice(0, 10)` is the obvious way and it is wrong
 * here. It gives the calendar day in **UTC**, and Cedar City is UTC-6 in summer
 * — so from about 6pm local onwards it returns tomorrow. A client taken on at
 * 7pm on 3 August was recorded as a customer since 4 August, which is exactly
 * how it was found: by creating one in the evening and reading the screen.
 *
 * The server runs in UTC deliberately, so this cannot be left to the process
 * time zone either. The zone is stated.
 */

/**
 * Where Honeycomb operates. Cedar City, Iron County, Utah — a verified fact
 * from the public site (FINDINGS.md §7), not a guess.
 *
 * A single business in a single time zone is the truth today. If that ever
 * stops being true, this becomes a setting rather than a constant, and this
 * comment is the pointer to every place that assumed otherwise.
 */
export const BUSINESS_TIME_ZONE = 'America/Denver';

/**
 * Today's calendar day where the business is, as `YYYY-MM-DD`.
 *
 * `en-CA` because its short date format *is* ISO — `2026-08-03` — which avoids
 * assembling the string from parts and getting the padding wrong.
 */
export function todayInBusinessZone(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * `2026-08-04` → `4 Aug 2026`.
 *
 * Shared rather than browser-only, because the server writes calendar days into
 * notification bodies and emails, and a date that reads one way on the screen
 * and another in the email about it is a support call. `src/client/lib/dates.ts`
 * re-exports this as `formatDay`.
 *
 * Split by hand rather than through `new Date(value)`. Parsing a bare
 * `YYYY-MM-DD` makes it **midnight UTC**, which renders as the day before
 * anywhere west of Greenwich — Cedar City is UTC-6, so every calendar day would
 * display one day early. That is the class of bug the `date` column type exists
 * to avoid, undone at the last step.
 */
export function formatCalendarDay(value: string): string {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;

  return `${String(day)} ${MONTHS[month - 1] ?? ''} ${String(year)}`;
}

/**
 * A wall-clock `HH:MM[:SS]` → `9:00 AM`.
 *
 * Moved here from `src/client/lib/dates.ts` in 11.2, for `formatCalendarDay`'s
 * reason: the server words job times into notifications now, and a time that
 * reads one way on the screen and another in the reminder about it is a
 * support call. Assembled from the digits, never through `new Date` — a `time`
 * column is a time of day in Cedar City, and wrapping it in a Date would shift
 * it by the viewer's zone.
 */
export function formatWallTime(value: string): string {
  const [hours, minutes] = value.split(':').map(Number);
  if (hours === undefined || minutes === undefined || Number.isNaN(hours)) return value;

  const period = hours < 12 ? 'AM' : 'PM';
  const clock = hours % 12 === 0 ? 12 : hours % 12;
  return `${String(clock)}:${String(minutes).padStart(2, '0')} ${period}`;
}

/**
 * A wall-clock moment in the business's zone, as the instant it names.
 *
 * "The crew finished at 2:30pm on the 1st" is 2:30pm in Cedar City, whatever
 * zone the server runs in — this is the conversion for a backdated completion
 * (business-rules.md §3 rule 4). `new Date("2026-08-01T14:30")` would parse in
 * the *server's* zone, which is UTC in production, and quietly record 8:30am.
 *
 * Two passes because the offset depends on the date being converted — the
 * whole reason wall-clock times are stored as `date` + `time` in the first
 * place. Pretend the wall clock is UTC, see what the business zone shows for
 * that instant, and correct by the difference. The second pass covers the
 * hour where a DST change makes the first correction land on the other side
 * of the boundary.
 */
export function businessWallClockToInstant(day: string, time: string): Date {
  const target = Date.parse(`${day}T${time}Z`);
  if (Number.isNaN(target)) throw new RangeError(`Not a date and time: ${day} ${time}`);

  let instant = target;
  for (let pass = 0; pass < 2; pass += 1) {
    const shown = shownInBusinessZone(new Date(instant));
    instant += target - shown;
  }
  return new Date(instant);
}

/** What the business-zone wall clock reads at `instant`, encoded as if UTC. */
function shownInBusinessZone(instant: Date): number {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(instant);

  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '00';
  return Date.parse(
    `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}Z`,
  );
}
